"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { 
  Plus, 
  FileText, 
  Search, 
  Copy, 
  MoreHorizontal, 
  Send, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  FileCheck,
  Download,
  Filter,
  ArrowUpRight
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CreateInvoiceModal } from "@/components/modals/create-invoice-modal";
import { InvoiceDetailsModal } from "@/components/modals/invoice-details-modal";

const initialInvoices = [
  {
    id: "INV-2026-0042",
    customer: "Global Logistics Tech",
    email: "finance@globallogistics.com",
    amount: "₦1,250,000.00",
    subtotal: 1250000,
    dueDate: "Oct 14, 2026",
    createdDate: "Today",
    status: "Pending",
    type: "warning",
    items: [
      { description: "API Integration & Virtual Account Setup", quantity: 1, unitPrice: 1250000 }
    ]
  },
  {
    id: "INV-2026-0041",
    customer: "Acme Retail Ltd",
    email: "accounts@acmeretail.ng",
    amount: "₦450,000.00",
    subtotal: 450000,
    dueDate: "Oct 10, 2026",
    createdDate: "Yesterday",
    status: "Paid",
    type: "success",
    items: [
      { description: "Monthly Payment Gateway Fee", quantity: 1, unitPrice: 450000 }
    ]
  },
  {
    id: "INV-2026-0039",
    customer: "Lexicon Enterprises",
    email: "billing@lexicon.com",
    amount: "₦3,100,000.00",
    subtotal: 3100000,
    dueDate: "Oct 01, 2026",
    createdDate: "Sep 20, 2026",
    status: "Overdue",
    type: "destructive",
    items: [
      { description: "Custom Settlement Automation Module", quantity: 1, unitPrice: 3100000 }
    ]
  },
  {
    id: "INV-2026-0038",
    customer: "TechStar Incubator",
    email: "info@techstar.io",
    amount: "₦850,000.00",
    subtotal: 850000,
    dueDate: "Oct 28, 2026",
    createdDate: "Sep 28, 2026",
    status: "Paid",
    type: "success",
    items: [
      { description: "Developer API Consultancy", quantity: 1, unitPrice: 850000 }
    ]
  },
  {
    id: "INV-2026-0035",
    customer: "Goodies SmartPay Ltd",
    email: "finance@goodies.com",
    amount: "₦620,000.00",
    subtotal: 620000,
    dueDate: "Nov 05, 2026",
    createdDate: "Oct 02, 2026",
    status: "Draft",
    type: "muted",
    items: [
      { description: "Rider Wallet Disburse Infrastructure", quantity: 1, unitPrice: 620000 }
    ]
  }
];

export default function InvoicingPage() {
  const [invoices, setInvoices] = useState(initialInvoices);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const handleInvoiceCreated = (newInv: any) => {
    setInvoices([newInv, ...invoices]);
  };

  const handleViewInvoice = (inv: any) => {
    setSelectedInvoice(inv);
    setIsDetailsOpen(true);
  };

  const handleMarkPaid = (id: string) => {
    setInvoices(invoices.map(inv => {
      if (inv.id === id) {
        return { ...inv, status: "Paid", type: "success" };
      }
      return inv;
    }));
  };

  const handleCopyLink = (id: string) => {
    navigator.clipboard.writeText(`https://checkout.paytaara.com/pay/${id.toLowerCase()}`);
    alert(`Checkout link for ${id} copied to clipboard!`);
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.customer.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          inv.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          inv.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (activeTab === "paid") return matchesSearch && inv.status === "Paid";
    if (activeTab === "pending") return matchesSearch && inv.status === "Pending";
    if (activeTab === "overdue") return matchesSearch && inv.status === "Overdue";
    if (activeTab === "draft") return matchesSearch && inv.status === "Draft";
    return matchesSearch;
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Invoicing & Collections</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Generate invoices, send instant payment links, and track auto-reconciled dedicated NUBANs.
          </p>
        </div>
        
        <CreateInvoiceModal onInvoiceCreated={handleInvoiceCreated}>
          <Button className="bg-primary text-primary-foreground font-bold text-xs h-10 px-5 shadow-sm">
            <Plus className="mr-2 h-4 w-4" /> Create New Invoice
          </Button>
        </CreateInvoiceModal>
      </div>

      {/* Analytics Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Total Invoiced", value: "₦6,270,000", desc: "5 active invoices", icon: FileText, color: "text-primary bg-primary/10" },
          { label: "Collected Amount", value: "₦1,300,000", desc: "Successfully settled", icon: CheckCircle2, color: "text-emerald-600 bg-emerald-500/10" },
          { label: "Pending Collections", value: "₦1,250,000", desc: "Awaiting payment", icon: Clock, color: "text-amber-600 bg-amber-500/10" },
          { label: "Overdue Amount", value: "₦3,100,000", desc: "Action required", icon: AlertCircle, color: "text-destructive bg-destructive/10" },
        ].map((stat) => (
          <Card key={stat.label} className="border shadow-sm">
            <div className="p-4 flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{stat.label}</p>
                <p className="text-xl font-bold tracking-tight">{stat.value}</p>
                <p className="text-[11px] text-muted-foreground">{stat.desc}</p>
              </div>
              <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", stat.color)}>
                <stat.icon className="h-4 w-4" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Main Invoices Table Card */}
      <Card className="border shadow-sm overflow-hidden">
        
        {/* Table Filter Header */}
        <div className="p-4 border-b bg-muted/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full sm:w-auto">
            <TabsList className="h-9 bg-muted/50 p-1 border rounded-lg">
              <TabsTrigger value="all" className="text-xs font-semibold px-3 h-7">All ({invoices.length})</TabsTrigger>
              <TabsTrigger value="pending" className="text-xs font-semibold px-3 h-7">Pending</TabsTrigger>
              <TabsTrigger value="paid" className="text-xs font-semibold px-3 h-7">Paid</TabsTrigger>
              <TabsTrigger value="overdue" className="text-xs font-semibold px-3 h-7">Overdue</TabsTrigger>
              <TabsTrigger value="draft" className="text-xs font-semibold px-3 h-7">Drafts</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input 
              placeholder="Search invoice or customer..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-9 text-xs bg-background border-border" 
            />
          </div>
        </div>

        {/* Invoice Table */}
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead className="h-10 text-xs font-bold px-5">Invoice Reference</TableHead>
              <TableHead className="h-10 text-xs font-bold">Customer</TableHead>
              <TableHead className="h-10 text-xs font-bold">Amount Due</TableHead>
              <TableHead className="h-10 text-xs font-bold">Due Date</TableHead>
              <TableHead className="h-10 text-xs font-bold">Status</TableHead>
              <TableHead className="h-10 text-xs font-bold text-right px-5">Action</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {filteredInvoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-xs text-muted-foreground">
                  No invoices found matching your filter criteria.
                </TableCell>
              </TableRow>
            ) : (
              filteredInvoices.map((inv) => (
                <TableRow key={inv.id} className="hover:bg-muted/10 transition-colors border-b">
                  <TableCell className="px-5 py-3.5">
                    <div className="font-mono text-xs font-bold text-foreground">{inv.id}</div>
                    <div className="text-[10px] text-muted-foreground">Created {inv.createdDate}</div>
                  </TableCell>

                  <TableCell>
                    <div className="text-xs font-bold text-foreground">{inv.customer}</div>
                    <div className="text-[10px] text-muted-foreground">{inv.email}</div>
                  </TableCell>

                  <TableCell className="font-mono text-xs font-bold text-foreground">
                    {inv.amount}
                  </TableCell>

                  <TableCell className="text-xs text-muted-foreground">
                    {inv.dueDate}
                  </TableCell>

                  <TableCell>
                    <Badge className={cn(
                      "text-[9px] font-bold uppercase border-none px-2 py-0.5",
                      inv.status === "Paid" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400" :
                      inv.status === "Pending" ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400" :
                      inv.status === "Overdue" ? "bg-destructive/10 text-destructive dark:bg-destructive/20" :
                      "bg-muted text-muted-foreground"
                    )}>
                      {inv.status}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-right px-5">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-muted">
                          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align="end" className="w-48 text-xs font-medium">
                        <DropdownMenuItem className="cursor-pointer" onClick={() => handleViewInvoice(inv)}>
                          <FileText className="h-3.5 w-3.5 mr-2 text-primary" /> View Details & PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem className="cursor-pointer" onClick={() => handleCopyLink(inv.id)}>
                          <Copy className="h-3.5 w-3.5 mr-2" /> Copy Checkout Link
                        </DropdownMenuItem>
                        <DropdownMenuItem className="cursor-pointer" onClick={() => alert(`Email reminder sent for ${inv.id}`)}>
                          <Send className="h-3.5 w-3.5 mr-2" /> Send Email Reminder
                        </DropdownMenuItem>

                        {inv.status !== "Paid" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="cursor-pointer text-emerald-600 font-bold" onClick={() => handleMarkPaid(inv.id)}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-2" /> Mark as Paid
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal 
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        invoice={selectedInvoice}
      />
    </div>
  );
}
